<div class="cc">
	<!-- Header + week nav (Export KPI intentionally excluded) -->
	<div class="cc-hdr">
		<div>
			<h1>Command Center</h1>
			<p>{{ weekLabel }} — live production snapshot</p>
		</div>
		<div class="cc-weeknav">
			<button class="cc-nav-btn" (click)="prevWeek()" aria-label="Previous week"><i
					class="ri-arrow-left-s-line"></i></button>
			<span class="cc-week-lbl">{{ weekLabel }}</span>
			<button class="cc-nav-btn" (click)="nextWeek()" aria-label="Next week"><i
					class="ri-arrow-right-s-line"></i></button>
			<span *ngIf="loading" class="spinner-border spinner-border-sm text-secondary ms-2" role="status"></span>
		</div>
	</div>

	<!-- Snapshot banner (computed) -->
	<div class="cc-snapshot" *ngIf="!loading">
		<span class="cc-snapshot-icon">⚠️</span>
		<span>
			<strong>Snapshot:</strong>
			{{ leadsProduced ?? 0 }} leads produced ({{ formatCurrency(weekRevenue) }}). Need ~<strong>{{ perDayNeeded
				}}</strong>/day for {{ businessDaysLeft }} business day(s) to fulfill {{ totalLeadsOrdered }}-lead
			weekly order.
			<ng-container *ngIf="topAccount as t">
				<strong>{{ t.campaign_name }}</strong> ({{ t.leads_remaining }} needed, {{
				formatCurrency(topAccountValue()) }} value) is top priority.
			</ng-container>
		</span>
	</div>

	<!-- KPI cards -->
	<div class="cc-stats">
		<div class="cc-stat" style="--acc: var(--lms-accent)">
			<div class="cc-stat-lbl">Week Revenue</div>
			<div class="cc-stat-val">{{ formatCurrency(weekRevenue) }}</div>
			<div class="cc-stat-sub">vs {{ formatCurrency(projectedRevenue) }} projected</div>
		</div>
		<div class="cc-stat" style="--acc: var(--lms-primary)">
			<div class="cc-stat-lbl">Leads Produced</div>
			<div class="cc-stat-val">{{ leadsProduced ?? "—" }}</div>
			<div class="cc-stat-sub">of {{ totalLeadsOrdered }} ordered</div>
		</div>
		<div class="cc-stat" style="--acc: var(--lms-green)">
			<div class="cc-stat-lbl">Billable Rate</div>
			<div class="cc-stat-val">{{ formatPercent(billableRate) }}</div>
			<div class="cc-stat-sub">leads charged vs produced <span class="cc-pending-tag"
					title="Per-lead billable/produced counts not returned by billable_rate (percentage only).">X of Y
					pending</span></div>
		</div>
		<div class="cc-stat" style="--acc: var(--lms-orange)">
			<div class="cc-stat-lbl">Remaining</div>
			<div class="cc-stat-val">{{ leadsRemaining ?? "—" }}</div>
			<div class="cc-stat-sub">~{{ perDayNeeded }}/day needed</div>
		</div>
	</div>

	<!-- Priority Production Board -->
	<div class="cc-card">
		<div class="cc-card-hdr">
			<div class="cc-card-title">Priority Production Board — This Week</div>
			<span class="cc-pill cc-pill-gold" *ngIf="totalLeadsOrdered > 0">🔥 {{ totalLeadsOrdered }} leads
				ordered</span>
		</div>
		<div class="table-responsive">
			<table class="cc-table">
				<thead>
					<tr>
						<th>Account</th>
						<th class="num text-center">$/Lead</th>
						<th class="num text-center">Ordered</th>
						<!-- <th class="num text-center">Mon</th> -->
						<th class="num text-center">Produced</th>
						<th class="num text-center">Remaining</th>
						<th class="num text-center">Revenue</th>
						<th class="text-center">Status</th>
						<th>Health</th>
					</tr>
				</thead>
				<tbody>
					<tr *ngIf="!loading && productionBoard.length === 0">
						<td colspan="9" class="cc-empty">No data for this week</td>
					</tr>
					<tr *ngFor="let row of productionBoard">
						<td class="cc-acct">{{ row.campaign_name }}</td>
						<td class="num text-center">{{ formatCurrency(row.lead_cost) }}</td>
						<td class="num text-center">{{ row.ordered }}</td>
						<!-- Mon: pending — no daily field on priority_production_board; no clean per-campaign join -->
						<!-- <td class="num text-center"><span class="cc-pending-tag" title="Monday-only production isn't on priority_production_board; a clean per-campaign join isn't available yet.">pending</span></td> -->
						<td class="num text-center">{{ row.leads_produced }}</td>
						<td class="num text-center"
							[style.color]="row.leads_remaining >= row.ordered ? 'var(--lms-red)' : 'inherit'">{{
							row.leads_remaining }}</td>
						<td class="num text-center">{{ formatCurrency(row.leads_produced * row.lead_cost) }}</td>
						<td class="text-center">
							<span class="cc-pill" [ngClass]="statusPill(row)">{{ statusLabel(row) }}</span>
						</td>
						<!-- Health: explicit pending-backend placeholder (no fake data) -->
						<!-- <td>
							<span class="cc-health-pending" title="Client-trust health (pending confirmations · reschedules · replacements + at-risk/irritated) needs a per-campaign join + a confirmations-pending endpoint that don't exist yet."> <span class="cc-health-dot"></span> Pending </span>
						</td> -->
						<!-- Health -->
						<td>
							<span class="cc-health-badge" [ngClass]="getHealthClass(row.health)" [ngbTooltip]="healthTooltip" tooltipClass="cc-health-tooltip" container="body" placement="bottom">
								<span class="cc-health-dot"></span>
								{{ row.health === "At risk" ? "At-risk" : row.health }}
								<i class="ri-error-warning-fill cc-health-alert" *ngIf="row.health === 'At risk'"></i>
							</span>

							<ng-template #healthTooltip>
								<div class="cc-health-tooltip-content">
									<div class="cc-health-tooltip-row">
										<i class="ri-time-line"></i>
										<span class="cc-health-tooltip-label">Pending</span>
										<span class="cc-health-tooltip-value">{{ row.leads_remaining }}</span>
									</div>
									<div class="cc-health-tooltip-row">
										<i class="ri-calendar-event-line"></i>
										<span class="cc-health-tooltip-label">Reschedules</span>
										<span class="cc-health-tooltip-value">{{ row.reschedule_count }}</span>
									</div>
									<div class="cc-health-tooltip-row">
										<i class="ri-refresh-line"></i>
										<span class="cc-health-tooltip-label">Replacements</span>
										<span class="cc-health-tooltip-value">{{ row.replacements }}</span>
									</div>
								</div>
							</ng-template>
						</td>
					</tr>
				</tbody>
			</table>
		</div>
		<div class="cc-foot">
			{{ productionBoard.length }} accounts · <strong>{{ formatCurrency(weekRevenue) }}</strong> billed ·
			<span class="cc-muted">Health is a client-trust lens (pending backend), separate from order Status.</span>
		</div>
	</div>
</div>
